import React, { useState, useEffect, useRef } from 'react';
import { DocumentInfo } from '../../types';
import { listDocuments, uploadDocuments, updateDocumentMetadata, uploadURLMappings, deleteDocument, checkDocumentsExist } from '../../services/api';
import AutoMapModal from './AutoMapModal';
import './DocumentsSection.css';

interface DocumentsSectionProps {
  projectId: string;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ projectId }) => {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ display_name: string; public_url: string }>({
    display_name: '',
    public_url: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation state
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Replace confirmation state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [showReplaceModal, setShowReplaceModal] = useState(false);

  // Bulk URL import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [isUploadingCSV, setIsUploadingCSV] = useState(false);
  const [isAutoMapOpen, setIsAutoMapOpen] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listDocuments(projectId);
      setDocuments(response.documents);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDocuments();
  }, [projectId]);

  const processUpload = async (files: File[], replaceExisting: boolean = false) => {
    setIsUploading(true);
    setUploadErrors([]);

    try {
      const result = await uploadDocuments(projectId, files, replaceExisting);
      if (result.errors.length > 0) {
        setUploadErrors(result.errors);
      }
      if (result.replaced_count && result.replaced_count > 0) {
        setSuccess(`Replaced ${result.replaced_count} document(s)`);
        setTimeout(() => setSuccess(null), 5000);
      }
      await loadDocuments();
    } catch (err: any) {
      setUploadErrors([err.response?.data?.detail || 'Upload failed']);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach(file => {
      const ext = file.name.toLowerCase();
      if (ext.endsWith('.pdf') || ext.endsWith('.zip')) {
        validFiles.push(file);
      } else {
        invalidFiles.push(`${file.name}: Only PDF and ZIP files are accepted`);
      }
    });

    if (invalidFiles.length > 0) {
      setUploadErrors(invalidFiles);
    }

    if (validFiles.length === 0) return;

    // Check for existing files
    try {
      const pdfFilenames = validFiles
        .filter(f => f.name.toLowerCase().endsWith('.pdf'))
        .map(f => f.name);

      if (pdfFilenames.length > 0) {
        const existsResult = await checkDocumentsExist(projectId, pdfFilenames);
        if (existsResult.existing_files.length > 0) {
          setPendingFiles(validFiles);
          setExistingFiles(existsResult.existing_files);
          setShowReplaceModal(true);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      }
    } catch (err) {
      // If check fails, proceed with normal upload
      console.warn('Could not check for existing files:', err);
    }

    await processUpload(validFiles);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);

    const validFiles = files.filter(f =>
      f.name.toLowerCase().endsWith('.pdf') || f.name.toLowerCase().endsWith('.zip')
    );

    if (validFiles.length === 0) {
      setUploadErrors(['Only PDF and ZIP files are accepted']);
      return;
    }

    // Check for existing files
    try {
      const pdfFilenames = validFiles
        .filter(f => f.name.toLowerCase().endsWith('.pdf'))
        .map(f => f.name);

      if (pdfFilenames.length > 0) {
        const existsResult = await checkDocumentsExist(projectId, pdfFilenames);
        if (existsResult.existing_files.length > 0) {
          setPendingFiles(validFiles);
          setExistingFiles(existsResult.existing_files);
          setShowReplaceModal(true);
          return;
        }
      }
    } catch (err) {
      console.warn('Could not check for existing files:', err);
    }

    await processUpload(validFiles);
  };

  const handleReplaceConfirm = async (replace: boolean) => {
    setShowReplaceModal(false);
    if (replace) {
      await processUpload(pendingFiles, true);
    } else {
      // Upload only non-existing files
      const newFiles = pendingFiles.filter(f => !existingFiles.includes(f.name));
      if (newFiles.length > 0) {
        await processUpload(newFiles, false);
      } else {
        setUploadErrors(['All files already exist. Use "Replace" to update them.']);
      }
    }
    setPendingFiles([]);
    setExistingFiles([]);
  };

  const cancelReplace = () => {
    setShowReplaceModal(false);
    setPendingFiles([]);
    setExistingFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const startEditing = (doc: DocumentInfo) => {
    setEditingDoc(doc.filename);
    setEditValues({
      display_name: doc.display_name,
      public_url: doc.public_url || ''
    });
  };

  const saveEdit = async () => {
    if (!editingDoc) return;

    try {
      await updateDocumentMetadata(projectId, editingDoc, editValues);
      await loadDocuments();
      setEditingDoc(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update document');
    }
  };

  const cancelEdit = () => {
    setEditingDoc(null);
    setEditValues({ display_name: '', public_url: '' });
  };

  // Delete handlers
  const startDelete = (filename: string) => {
    setDeletingDoc(filename);
  };

  const confirmDelete = async () => {
    if (!deletingDoc) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteDocument(projectId, deletingDoc);
      setSuccess(`Document "${deletingDoc}" deleted successfully`);
      setTimeout(() => setSuccess(null), 5000);
      await loadDocuments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setIsDeleting(false);
      setDeletingDoc(null);
    }
  };

  const cancelDelete = () => {
    setDeletingDoc(null);
  };

  // CSV upload handler for bulk URL import
  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setIsUploadingCSV(true);
    setError(null);

    try {
      await uploadURLMappings(projectId, file);
      setSuccess('URL mapping CSV uploaded. Click "Auto-map URLs" to match documents.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setIsUploadingCSV(false);
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    }
  };

  const handleAutoMapSaved = () => {
    setSuccess('URL mappings saved successfully!');
    loadDocuments();
    setTimeout(() => setSuccess(null), 3000);
  };

  // Count docs with URLs
  const docsWithUrls = documents.filter(d => d.public_url).length;

  return (
    <div className="documents-section">
      <div className="section-header">
        <h3>📄 Policy Documents</h3>
        <p className="section-description">
          Upload your company's policy documents. These are used to answer compliance questionnaires.
        </p>
      </div>

      {/* Messages */}
      {success && <div className="success-message">✅ {success}</div>}
      {error && <div className="error-message">⚠️ {error}</div>}

      {/* Replace Confirmation Modal */}
      {showReplaceModal && (
        <div className="replace-modal-overlay">
          <div className="replace-modal">
            <h4>Files Already Exist</h4>
            <p>The following files already exist in your project:</p>
            <ul className="existing-files-list">
              {existingFiles.map((f, idx) => (
                <li key={idx}>📄 {f}</li>
              ))}
            </ul>
            <p className="replace-warning">
              ⚠️ Replacing will delete existing embeddings and regenerate them from the new content.
            </p>
            <div className="replace-modal-actions">
              <button onClick={cancelReplace} className="cancel-btn">
                Cancel
              </button>
              <button onClick={() => handleReplaceConfirm(false)} className="skip-btn">
                Skip Existing
              </button>
              <button onClick={() => handleReplaceConfirm(true)} className="replace-btn">
                Replace All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`upload-zone ${isUploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !isUploading && fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept=".pdf,.zip"
          multiple
          onChange={handleFileSelect}
          hidden
        />
        {isUploading ? (
          <div className="upload-status">
            <span className="spinner">⏳</span>
            <span>Uploading documents...</span>
          </div>
        ) : (
          <div className="upload-prompt">
            <span className="upload-icon">📁</span>
            <span>Drag & drop PDF or ZIP files here, or click to browse</span>
            <span className="upload-hint">Accepts PDF files or ZIP archives containing PDFs</span>
          </div>
        )}
      </div>

      {/* Upload Errors */}
      {uploadErrors.length > 0 && (
        <div className="upload-errors">
          {uploadErrors.map((err, idx) => (
            <div key={idx} className="error-item">⚠️ {err}</div>
          ))}
        </div>
      )}

      {/* Documents List */}
      <div className="documents-list">
        <div className="list-header">
          <h4>
            Uploaded Documents ({documents.length})
            {docsWithUrls > 0 && (
              <span className="url-count"> • {docsWithUrls} with public URLs</span>
            )}
          </h4>
        </div>

        {isLoading ? (
          <div className="loading">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <p>No documents uploaded yet</p>
            <p className="empty-hint">Upload your policy PDFs to get started</p>
          </div>
        ) : (
          <>
            <table className="documents-table">
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Filename</th>
                  <th>Evidence URL <span className="optional-badge">optional</span></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.filename}>
                    {editingDoc === doc.filename ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editValues.display_name}
                            onChange={(e) => setEditValues({ ...editValues, display_name: e.target.value })}
                            className="edit-input"
                          />
                        </td>
                        <td className="filename">{doc.filename}</td>
                        <td>
                          <input
                            type="text"
                            value={editValues.public_url}
                            onChange={(e) => setEditValues({ ...editValues, public_url: e.target.value })}
                            className="edit-input url-input"
                            placeholder="https://drive.google.com/..."
                          />
                        </td>
                        <td className="actions">
                          <button onClick={saveEdit} className="save-btn">✓ Save</button>
                          <button onClick={cancelEdit} className="cancel-btn">✕</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{doc.display_name}</td>
                        <td className="filename">{doc.filename}</td>
                        <td>
                          {doc.public_url ? (
                            <a href={doc.public_url} target="_blank" rel="noopener noreferrer" className="url-link">
                              🔗 {doc.public_url.length > 40 ? doc.public_url.substring(0, 40) + '...' : doc.public_url}
                            </a>
                          ) : (
                            <span className="no-url">—</span>
                          )}
                        </td>
                        <td className="actions">
                          <button onClick={() => startEditing(doc)} className="edit-btn">✏️</button>
                          <button
                            onClick={() => startDelete(doc.filename)}
                            className="delete-btn"
                            title="Delete document"
                          >
                            🗑️
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Delete Confirmation Modal */}
            {deletingDoc && (
              <div className="delete-modal-overlay">
                <div className="delete-modal">
                  <h4>Delete Document?</h4>
                  <p>
                    Are you sure you want to delete <strong>"{deletingDoc}"</strong>?
                  </p>
                  <p className="delete-warning">
                    ⚠️ This will also remove all embeddings for this document from the vector store.
                  </p>
                  <div className="delete-modal-actions">
                    <button
                      onClick={cancelDelete}
                      className="cancel-btn"
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      className="confirm-delete-btn"
                      disabled={isDeleting}
                    >
                      {isDeleting ? '⏳ Deleting...' : '🗑️ Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk URL Import Section - Collapsible */}
            <div className="bulk-import-section">
              <button
                className="bulk-import-toggle"
                onClick={() => setShowBulkImport(!showBulkImport)}
              >
                {showBulkImport ? '▼' : '▶'} Bulk Import URLs from CSV
                <span className="toggle-hint">
                  {showBulkImport ? '' : '(have a spreadsheet with document URLs?)'}
                </span>
              </button>

              {showBulkImport && (
                <div className="bulk-import-content">
                  <p className="bulk-import-desc">
                    Upload a CSV file with document names and their public URLs.
                    We'll automatically match them to your uploaded documents.
                  </p>

                  <div className="bulk-import-actions">
                    <label className="csv-upload-btn">
                      <input
                        type="file"
                        ref={csvInputRef}
                        accept=".csv"
                        onChange={handleCSVUpload}
                        hidden
                      />
                      {isUploadingCSV ? '⏳ Uploading...' : '📄 Upload CSV'}
                    </label>

                    <button
                      className="auto-map-btn"
                      onClick={() => setIsAutoMapOpen(true)}
                    >
                      🔄 Auto-map URLs
                    </button>
                  </div>

                  <div className="csv-format-hint">
                    <strong>CSV Format:</strong>
                    <pre>{`File name,URL
Privacy Policy.pdf,https://drive.google.com/file/d/abc123
Security Policy.pdf,https://drive.google.com/file/d/def456`}</pre>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Auto-map Modal */}
      <AutoMapModal
        projectId={projectId}
        isOpen={isAutoMapOpen}
        onClose={() => setIsAutoMapOpen(false)}
        onSaved={handleAutoMapSaved}
      />
    </div>
  );
};

export default DocumentsSection;

